import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Upload, FileText, Download, Loader2, Trash2, ChevronLeft, ChevronRight, Save, Edit3, AlertCircle, CheckCircle, Square, X, MousePointer, Undo2 } from 'lucide-react';
import axios from 'axios';
import '../styles/PdfTextEditor.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PdfTextEditor = () => {
  const [file, setFile] = useState(null);
  const [pages, setPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [editedTexts, setEditedTexts] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [scale, setScale] = useState(1);
  const [activeItemId, setActiveItemId] = useState(null);
  const [totalPages, setTotalPages] = useState(0);

  // Whiteout state
  const [whiteoutMode, setWhiteoutMode] = useState(false);
  const [whiteouts, setWhiteouts] = useState([]); // {id, page_num, x, y, w, h} in image coords
  const [drawing, setDrawing] = useState(null); // {startX, startY, currentX, currentY} in display coords
  const [selectedWhiteout, setSelectedWhiteout] = useState(null);

  // Undo history
  const [history, setHistory] = useState([]);

  const pushHistory = (curEdits, curWhiteouts) => {
    setHistory(prev => [...prev, {
      editedTexts: { ...curEdits },
      whiteouts: [...curWhiteouts],
    }]);
  };

  const handleUndo = () => {
    if (history.length === 0) return;
    const prev = history[history.length - 1];
    setEditedTexts(prev.editedTexts);
    setWhiteouts(prev.whiteouts);
    setHistory(h => h.slice(0, -1));
    setSelectedWhiteout(null);
  };

  const fileInputRef = useRef(null);
  const containerRef = useRef(null);
  const pageContainerRef = useRef(null);

  const handleFileSelect = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    if (selected.type !== 'application/pdf') {
      setError('Please upload a PDF file only.');
      return;
    }
    if (selected.size > 20 * 1024 * 1024) {
      setError('File size must be less than 20MB.');
      return;
    }
    setFile(selected);
    setError('');
    setSuccess('');
    setPages([]);
    setEditedTexts({});
    setCurrentPage(0);
    setSessionId(null);
    setWhiteouts([]);
    setWhiteoutMode(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) handleFileSelect({ target: { files: [dropped] } });
  };

  const handleUploadAndExtract = useCallback(async () => {
    if (!file) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${BACKEND_URL}/api/client/pdfeditor/extract-text`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      setSessionId(res.data.session_id);
      setPages(res.data.pages);
      setTotalPages(res.data.total_pages);
      setCurrentPage(0);
      setEditedTexts({});
      setWhiteouts([]);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to process PDF. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [file]);

  useEffect(() => {
    if (file && !sessionId && !loading) {
      handleUploadAndExtract();
    }
  }, [file, sessionId, loading, handleUploadAndExtract]);

  const handleImageLoad = (e) => {
    const naturalW = e.target.naturalWidth;
    const displayedW = e.target.clientWidth;
    if (naturalW > 0) {
      setScale(displayedW / naturalW);
    }
  };

  const handleTextEdit = (itemId, newText) => {
    const page = pages[currentPage];
    if (!page) return;
    const original = page.text_items.find(i => i.id === itemId);
    if (!original) return;
    if (newText === original.text) {
      if (editedTexts.hasOwnProperty(itemId)) {
        pushHistory(editedTexts, whiteouts);
        setEditedTexts(prev => {
          const next = { ...prev };
          delete next[itemId];
          return next;
        });
      }
    } else {
      pushHistory(editedTexts, whiteouts);
      setEditedTexts(prev => ({ ...prev, [itemId]: newText }));
    }
  };

  // Whiteout drawing handlers
  const getRelativePos = (e) => {
    const container = pageContainerRef.current;
    if (!container) return { x: 0, y: 0 };
    const rect = container.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const handleMouseDown = (e) => {
    if (!whiteoutMode) return;
    e.preventDefault();
    const pos = getRelativePos(e);
    setDrawing({ startX: pos.x, startY: pos.y, currentX: pos.x, currentY: pos.y });
    setSelectedWhiteout(null);
  };

  const handleMouseMove = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const pos = getRelativePos(e);
    setDrawing(prev => ({ ...prev, currentX: pos.x, currentY: pos.y }));
  };

  const handleMouseUp = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const pos = getRelativePos(e);
    const x1 = Math.min(drawing.startX, pos.x);
    const y1 = Math.min(drawing.startY, pos.y);
    const x2 = Math.max(drawing.startX, pos.x);
    const y2 = Math.max(drawing.startY, pos.y);
    const w = x2 - x1;
    const h = y2 - y1;

    // Only add if rectangle is big enough (at least 5x5 display pixels)
    if (w > 5 && h > 5) {
      const newWhiteout = {
        id: `wo_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        page_num: currentPage,
        x: x1 / scale,
        y: y1 / scale,
        w: w / scale,
        h: h / scale,
      };
      pushHistory(editedTexts, whiteouts);
      setWhiteouts(prev => [...prev, newWhiteout]);
    }
    setDrawing(null);
  };

  const deleteWhiteout = (woId) => {
    pushHistory(editedTexts, whiteouts);
    setWhiteouts(prev => prev.filter(w => w.id !== woId));
    setSelectedWhiteout(null);
  };

  const currentPageWhiteouts = whiteouts.filter(w => w.page_num === currentPage);

  const handleSaveAndDownload = async () => {
    const hasEdits = Object.keys(editedTexts).length > 0;
    const hasWhiteouts = whiteouts.length > 0;
    if (!sessionId || (!hasEdits && !hasWhiteouts)) return;

    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const edits = [];
      for (const [itemId, newText] of Object.entries(editedTexts)) {
        for (const page of pages) {
          const item = page.text_items.find(i => i.id === itemId);
          if (item) {
            edits.push({
              page_num: page.page_num,
              new_text: newText,
              pdf_bbox: item.pdf_bbox,
              pdf_font_size: item.pdf_font_size,
              pdf_font: item.pdf_font,
              pdf_color: item.pdf_color,
            });
            break;
          }
        }
      }

      // Convert whiteout image coords to PDF coords
      const dpiScale = 150 / 72; // image pixels per PDF point
      const whiteoutData = whiteouts.map(w => ({
        page_num: w.page_num,
        pdf_rect: [
          w.x / dpiScale,
          w.y / dpiScale,
          (w.x + w.w) / dpiScale,
          (w.y + w.h) / dpiScale,
        ],
      }));

      const res = await axios.post(`${BACKEND_URL}/api/client/pdfeditor/apply-edits`, {
        session_id: sessionId,
        edits,
        whiteouts: whiteoutData,
      }, { timeout: 60000 });

      if (res.data.success) {
        const link = document.createElement('a');
        link.href = `${BACKEND_URL}${res.data.download_url}`;
        link.download = res.data.filename || 'edited.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        const totalChanges = edits.length + whiteouts.length;
        setSuccess(`PDF saved! ${totalChanges} change(s) applied.`);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save edits.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setPages([]);
    setCurrentPage(0);
    setSessionId(null);
    setEditedTexts({});
    setError('');
    setSuccess('');
    setScale(1);
    setActiveItemId(null);
    setWhiteouts([]);
    setWhiteoutMode(false);
    setDrawing(null);
    setSelectedWhiteout(null);
    setHistory([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const editCount = Object.keys(editedTexts).length;
  const totalChanges = editCount + whiteouts.length;
  const currentPageData = pages[currentPage];

  useEffect(() => {
    const handleResize = () => {
      const img = containerRef.current?.querySelector('.pdf-page-image');
      if (img && img.naturalWidth > 0) {
        setScale(img.clientWidth / img.naturalWidth);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get drawing preview rect in display coords
  const drawingRect = drawing ? {
    x: Math.min(drawing.startX, drawing.currentX),
    y: Math.min(drawing.startY, drawing.currentY),
    w: Math.abs(drawing.currentX - drawing.startX),
    h: Math.abs(drawing.currentY - drawing.startY),
  } : null;

  return (
    <div className="pdf-text-editor" data-testid="pdf-text-editor">
      {!file && (
        <div
          className="pte-dropzone"
          data-testid="pte-dropzone"
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
        >
          <Upload size={48} className="dropzone-icon" />
          <p className="dropzone-title">Drop your PDF here or click to browse</p>
          <p className="dropzone-hint">Upload a PDF to edit its text (max 20MB)</p>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        data-testid="pte-file-input"
      />

      {loading && (
        <div className="pte-loading" data-testid="pte-loading">
          <Loader2 size={40} className="spin" />
          <p>Extracting text from PDF...</p>
          <p className="pte-loading-hint">This may take a moment for large files</p>
        </div>
      )}

      {currentPageData && !loading && (
        <div className="pte-editor-area">
          <div className="pte-toolbar" data-testid="pte-toolbar">
            <div className="pte-toolbar-left">
              <FileText size={16} />
              <span className="pte-filename">{file?.name}</span>
              <span className="pte-page-info">
                {totalPages} page{totalPages !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="pte-toolbar-center">
              {/* Mode Toggle */}
              <button
                className={`pte-mode-btn${!whiteoutMode ? ' active' : ''}`}
                onClick={() => setWhiteoutMode(false)}
                title="Text Edit Mode"
                data-testid="pte-mode-text"
              >
                <MousePointer size={16} /> Text
              </button>
              <button
                className={`pte-mode-btn pte-mode-whiteout${whiteoutMode ? ' active' : ''}`}
                onClick={() => setWhiteoutMode(true)}
                title="Whiteout Mode - drag to cover areas"
                data-testid="pte-mode-whiteout"
              >
                <Square size={16} /> Whiteout
              </button>

              <span className="pte-toolbar-divider" />

              <button
                className="pte-nav-btn"
                onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                disabled={currentPage === 0}
                data-testid="pte-prev-page"
              >
                <ChevronLeft size={18} />
              </button>
              <span className="pte-page-num" data-testid="pte-page-indicator">
                Page {currentPage + 1} / {totalPages}
              </span>
              <button
                className="pte-nav-btn"
                onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={currentPage >= totalPages - 1}
                data-testid="pte-next-page"
              >
                <ChevronRight size={18} />
              </button>
            </div>
            <div className="pte-toolbar-right">
              <button
                className="pte-undo-btn"
                onClick={handleUndo}
                disabled={history.length === 0}
                title="Undo last action"
                data-testid="pte-undo-btn"
              >
                <Undo2 size={16} />
              </button>
              {totalChanges > 0 && (
                <span className="pte-edit-badge" data-testid="pte-edit-count">
                  <Edit3 size={13} /> {totalChanges} change{totalChanges !== 1 ? 's' : ''}
                </span>
              )}
              <button
                className="pte-save-btn"
                onClick={handleSaveAndDownload}
                disabled={totalChanges === 0 || saving}
                data-testid="pte-save-btn"
              >
                {saving ? (
                  <><Loader2 size={16} className="spin" /> Saving...</>
                ) : (
                  <><Save size={16} /> Save & Download</>
                )}
              </button>
              <button className="pte-reset-btn" onClick={handleReset} data-testid="pte-reset-btn">
                <Trash2 size={16} /> New File
              </button>
            </div>
          </div>

          {/* Page Viewer */}
          <div className="pte-page-viewer" ref={containerRef}>
            <div
              className={`pte-page-container${whiteoutMode ? ' whiteout-cursor' : ''}`}
              ref={pageContainerRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={() => { if (drawing) setDrawing(null); }}
            >
              <img
                src={`${BACKEND_URL}${currentPageData.image_url}`}
                alt={`Page ${currentPage + 1}`}
                className="pdf-page-image"
                onLoad={handleImageLoad}
                draggable={false}
                data-testid="pte-page-image"
              />

              {/* Text Layer - only interactive when NOT in whiteout mode */}
              <div className={`pte-text-layer${whiteoutMode ? ' disabled' : ''}`}>
                {currentPageData.text_items.map((item) => {
                  const isEdited = editedTexts.hasOwnProperty(item.id);
                  const isActive = activeItemId === item.id;
                  const displayText = isEdited ? editedTexts[item.id] : item.text;

                  return (
                    <div
                      key={item.id}
                      className={`pte-text-item${isEdited ? ' edited' : ''}${isActive ? ' active' : ''}`}
                      style={{
                        left: `${item.x * scale}px`,
                        top: `${item.y * scale}px`,
                        minWidth: `${item.w * scale}px`,
                        height: `${item.h * scale}px`,
                        fontSize: `${Math.max(8, item.font_size * scale)}px`,
                        lineHeight: `${item.h * scale}px`,
                      }}
                      contentEditable={!whiteoutMode}
                      suppressContentEditableWarning
                      spellCheck={false}
                      data-testid={`pte-text-${item.id}`}
                      onFocus={() => !whiteoutMode && setActiveItemId(item.id)}
                      onBlur={(e) => {
                        setActiveItemId(null);
                        handleTextEdit(item.id, e.target.textContent);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          e.target.blur();
                        }
                      }}
                    >
                      {displayText}
                    </div>
                  );
                })}
              </div>

              {/* Whiteout Rectangles */}
              {currentPageWhiteouts.map((wo) => (
                <div
                  key={wo.id}
                  className={`pte-whiteout-rect${selectedWhiteout === wo.id ? ' selected' : ''}`}
                  data-testid={`pte-whiteout-${wo.id}`}
                  style={{
                    left: `${wo.x * scale}px`,
                    top: `${wo.y * scale}px`,
                    width: `${wo.w * scale}px`,
                    height: `${wo.h * scale}px`,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedWhiteout(selectedWhiteout === wo.id ? null : wo.id);
                  }}
                >
                  {selectedWhiteout === wo.id && (
                    <button
                      className="pte-whiteout-delete"
                      onClick={(e) => { e.stopPropagation(); deleteWhiteout(wo.id); }}
                      data-testid={`pte-whiteout-delete-${wo.id}`}
                      title="Remove whiteout"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}

              {/* Drawing Preview */}
              {drawingRect && drawingRect.w > 2 && drawingRect.h > 2 && (
                <div
                  className="pte-whiteout-preview"
                  style={{
                    left: `${drawingRect.x}px`,
                    top: `${drawingRect.y}px`,
                    width: `${drawingRect.w}px`,
                    height: `${drawingRect.h}px`,
                  }}
                />
              )}
            </div>
          </div>

          <p className="pte-help-text">
            {whiteoutMode
              ? 'Whiteout mode: Click and drag on the PDF to cover areas with white. Click a whiteout to select and delete it.'
              : 'Click on any text to edit it. Changed text is highlighted. Press Enter or click outside to confirm.'
            }
          </p>
        </div>
      )}

      {pages.length > 0 && !loading && currentPageData && currentPageData.text_items.length === 0 && (
        <div className="pte-no-text" data-testid="pte-no-text">
          <AlertCircle size={24} />
          <p>No editable text found on this page. It may be a scanned/image-based PDF.</p>
        </div>
      )}

      {error && (
        <div className="pte-error" data-testid="pte-error">
          <AlertCircle size={18} /><span>{error}</span>
        </div>
      )}

      {success && (
        <div className="pte-success" data-testid="pte-success">
          <CheckCircle size={18} /><span>{success}</span>
        </div>
      )}
    </div>
  );
};

export default PdfTextEditor;
